import axios from 'axios'
import router from '@/router'
import { responseHandlerService } from '@/plugins/responseHandlerService'
import { baseUrl } from '@/networking/urlManager'
import { errorMessages } from './errorMessages'

const DEFAULT_TIMEOUT = 500000

let stores = null

export const initializeAxiosStores = (authStore, uiStore) => {
  stores = { authStore, uiStore }
}

const instance = axios.create({
  baseURL: baseUrl,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

instance.CancelToken = axios.CancelToken
instance.isCancel = axios.isCancel

instance.interceptors.request.use(
    (config) => {
      if (!stores) {
        console.warn('Stores not initialized in axios instance')
        return config
      }

      const { authStore, uiStore } = stores
      responseHandlerService.resetConnectionErrorFlag()
      errorMessages.setLanguage(uiStore.getCurrentLanguage)

      const token = authStore.userData?.token
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }

      config.headers['Accept-Language'] = uiStore.getCurrentLanguage
      config.headers.Timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Istanbul'

      return config
    },
    (error) => Promise.reject(error),
)

/**
 * Bağlantı hatasının olup olmadığını kontrol eder
 */
function isConnectionError(error) {
  return (
      // Zaman aşımı hataları (bu gerçek bağlantı sorunlarıdır)
      (error.code === 'ECONNABORTED' && error.message.includes('timeout')) ||
      // Açıkça "Network Error" içeren hatalar
      error.message?.includes('Network Error') ||
      // CORS veya diğer ağ hataları nedeniyle yanıt alınamadı
      (error.request && !error.response && !error.response?.status) ||
      // Sadece !error.response olması bir bağlantı hatası değildir, 
      // çünkü sunucu yanıt döndürebilir ama boş yanıt olabilir
      false 
  )
}

/**
 * Bağlantı hatalarını işler
 */
function handleConnectionError(error) {
  responseHandlerService.handleError({
    isConnectionError: true,
    message: errorMessages.get('connectionError'),
  })
  return Promise.reject(error)
}

// Token yenileme için kullanılan değişken - yenileme işlemi sırasında çoklu istekleri önler
let isRefreshing = false
let refreshSubscribers = []

/**
 * Token yenileme süreci tamamlandığında bekleyen istekleri tekrar gönderir
 */
function onRefreshed(newToken) {
  refreshSubscribers.forEach(callback => callback(newToken))
  refreshSubscribers = []
}

/**
 * Yenileme süreci sırasında bekleyecek istekleri kaydeder
 */
function addSubscriber(callback) {
  refreshSubscribers.push(callback)
}

/**
 * Token yenileme isteği gönderir
 */
async function refreshToken(authStore) {
  try {
    // Use the vanilla axios instead of our instance to avoid interceptor loops
    const response = await axios.post(`${baseUrl}/users/refresh-token`, {}, {
      headers: {
        Authorization: `Bearer ${authStore.userData?.token}`,
      },
      _skipAuthRedirect: true,
    })
    
    const { token } = response.data.data
    await authStore.updateToken(token)
    return token
  } catch (err) {
    // Token yenileme başarısız olursa oturumu kapat
    await authStore.clearUserData()
    await router.push('/')
    throw err
  }
}

/**
 * 401 Unauthorized hatalarını işler
 */
async function handle401Error(error, authStore) {
  // _skipAuthRedirect ayarlıysa doğrudan hatayı döndür
  if (error.config._skipAuthRedirect) {
    await authStore.clearUserData()
    return Promise.reject(error)
  }
  
  // Token'in süresi dolduğunda özel işlem
  const isTokenExpired = error.response.headers['x-token-expired'] === 'true' || 
                       error.response.data?.data?.tokenStatus === 'expired'
  
  if (isTokenExpired && authStore.userData?.token) {
    const originalRequest = error.config
    
    // Eğer zaten token yenileme işlemi yapılıyorsa bekle
    if (isRefreshing) {
      try {
        // Yeni token alınmayı bekle ve sonra isteği tekrarla
        const newToken = await new Promise(resolve => {
          addSubscriber(token => resolve(token))
        })
        
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return instance(originalRequest)
      } catch (err) {
        return Promise.reject(err)
      }
    }
    
    // Token yenileme işlemini başlat
    isRefreshing = true
    
    try {
      // Token yenileme isteği gönder
      const newToken = await refreshToken(authStore)
      
      // Orijinal isteği yeni token ile tekrarla
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      
      // Bekleyen istekleri bilgilendir
      onRefreshed(newToken)
      
      // Yenileme işlemini sıfırla
      isRefreshing = false
      
      // Orijinal isteği tekrarla
      return instance(originalRequest)
    } catch (refreshError) {
      // Yenileme başarısız olursa
      isRefreshing = false
      
      // Oturumu kapat ve giriş sayfasına yönlendir
      await authStore.clearUserData()
      await router.push('/')
      
      if (!responseHandlerService.unauthorizedSnackbarDisplayed) {
        const message = errorMessages.get('sessionExpired')
        responseHandlerService.handleError({ message })
        responseHandlerService.unauthorizedSnackbarDisplayed = true
      }
      
      return Promise.reject(refreshError)
    }
  }
  
  // Normal 401 hatası işleme
  await authStore.clearUserData()
  await router.push('/')

  if (!responseHandlerService.unauthorizedSnackbarDisplayed) {
    const message = error.response.data?.message || errorMessages.get('sessionExpired')
    responseHandlerService.handleError({ message })
    responseHandlerService.unauthorizedSnackbarDisplayed = true
  }
}

/**
 * 500 Server Error hatalarını işler
 */
function handle500Error(error) {
  const message = error.response.data?.message || errorMessages.get('serverError')
  responseHandlerService.handleError({ message })
}

/**
 * HTTP durum kodu hatalarını işler
 */
async function handleHttpErrors(error, stores) {
  const { authStore } = stores
  const status = error.response?.status

  // HTTP hatasını bağlantı hatası olarak işaretlemeyi engelle
  error.isConnectionError = false;

  switch (status) {
    case 401:
      await handle401Error(error, authStore)
      break
    case 500:
      handle500Error(error)
      break
    case 400:
      // 400 hataları için response.data içindeki mesajı kullan
      return Promise.reject({
        ...error,
        isConnectionError: false,
      })
    // Diğer HTTP hata kodları gerekirse buraya eklenebilir
    default:
      // Diğer tüm HTTP hataları için de bağlantı hatası olarak işaretlemeyi engelle
      return Promise.reject({
        ...error,
        isConnectionError: false
      })
  }

  return Promise.reject(error)
}

instance.interceptors.response.use(
    (response) => {
      responseHandlerService.resetConnectionErrorFlag()
      return response
    },
    async (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error)
      }

      // Bağlantı hatalarını ele al
      if (isConnectionError(error)) {
        return handleConnectionError(error)
      }

      if (!stores) {
        console.warn('Stores not initialized in axios instance')
        return Promise.reject(error)
      }

      // HTTP durum kodu hatalarını ele al
      return handleHttpErrors(error, stores)
    },
)

export default instance